import { urlParamsModule } from "@/modules/url-params";
import { INoteObservable } from "@/store/note/types";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { isEmpty, isString } from "lodash-es";
import { action, computed, makeObservable, observable } from "mobx";
import { ListStateObservable, ListStateProvider } from "@/store/pages/ListStateObservable";
import { EventContext } from "@/domains/metrics/context";

export type CalculateSelectedItemIndex = (args: {
  rowCount: number;
}) => number | { itemId: string };
export type RegisterSelectedItemHandler = (args: { handler?: () => void; itemId?: string }) => void;

export class QuickSearchModalStore extends AppSubStore implements ListStateProvider {
  readonly eventContext: EventContext = EventContext.QuickSearchActions;
  readonly listState: ListStateObservable;
  supportsChatListAction = true;

  orderedItemIds: string[] = [];
  isCollection: boolean[] = [];
  isOpen = false;
  isFocused = false;
  value = "";

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.listState = new ListStateObservable({
      ...injectedDeps,
      listStateProvider: this,
      canSelect: false,
    });

    makeObservable(this, {
      supportsChatListAction: true,
      eventContext: false,
      listState: false,
      orderedItemIds: observable,
      isCollection: observable,
      setOrderedItemIds: action,
      isOpen: observable,
      isFocused: observable,
      value: observable,
      shouldShowRecentsSection: computed,
      shouldShowSuggestionsSection: computed,
      selectedNoteObservable: computed,
      toggleIsOpen: action,
      openWithQuery: action,
      close: action,
      reset: action,
      handleRef: false,
      handleBlur: action,
      handleFocus: action,
      handleValueChange: action,
      highlightPrevious: action,
      highlightNext: action,
      selectHighlighted: action,
      handleSearch: action,
      handleAsk: action,
    });
  }

  get shouldShowRecentsSection() {
    return isEmpty(this.value);
  }

  get shouldShowSuggestionsSection() {
    return !isEmpty(this.value);
  }

  get selectedNoteObservable(): INoteObservable | undefined {
    if (!this.listState.highlightedItemId) {
      return undefined;
    }

    const noteObservable = this.store.notes.getNoteObservableById({
      noteId: this.listState.highlightedItemId,
    });

    return noteObservable;
  }

  setOrderedItemIds(orderedItemIds: string[], isCollection?: boolean[]) {
    this.orderedItemIds = orderedItemIds;
    this.isCollection = isCollection ?? [];

    // Auto-highlight first item when modal is open and items are available
    const firstItem = orderedItemIds[0];
    if (!this.isOpen || !firstItem) return;

    this.listState.highlightItem(firstItem);
  }

  toggleIsOpen = () => {
    this.reset();
    this.isOpen = !this.isOpen;
  };

  openWithQuery = ({ query }: { query: string | null }) => {
    if (isString(query)) {
      this.value = query;
    }

    this.isOpen = true;
  };

  close = () => {
    this.isOpen = false;
    this.reset();
  };

  reset = () => {
    this.value = "";
    this.listState.clearHoveredItem();
    this.listState.highlightItem();
  };

  handleRef = (elem: HTMLTextAreaElement | null) => {
    if (!elem) return;

    elem.focus();

    // Set cursor at the end of text
    const length = elem.value.length;
    elem.setSelectionRange(length, length);
  };

  handleBlur = (event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    this.isFocused = false;
    // Refocus.
    event.target.focus();
  };

  handleFocus = () => {
    this.isFocused = true;
  };

  handleValueChange = (value: string) => {
    this.listState.clearHoveredItem();
    this.listState.highlightItem();
    this.value = value;
  };

  highlightPrevious = () => {
    const index =
      typeof this.listState.highlightedItemId === "string"
        ? (this.orderedItemIds.indexOf(this.listState.highlightedItemId) +
            this.orderedItemIds.length -
            1) %
          this.orderedItemIds.length
        : 0;
    this.listState.highlightItem(this.orderedItemIds[index]);
  };

  highlightNext = () => {
    const index =
      typeof this.listState.highlightedItemId === "string"
        ? (this.orderedItemIds.indexOf(this.listState.highlightedItemId) +
            this.orderedItemIds.length +
            1) %
          this.orderedItemIds.length
        : 0;
    this.listState.highlightItem(this.orderedItemIds[index]);
  };

  selectHighlighted = (event: KeyboardEvent) => {
    const itemId = this.listState.highlightedItemId;
    if (!itemId) return;

    if (itemId.startsWith("search-")) {
      this.handleSearch();
    } else if (itemId.startsWith("ask-")) {
      this.handleAsk();
    } else if (this.isCollection[this.orderedItemIds.indexOf(itemId)]) {
      this.store.navigation.goToCollection({ collectionId: itemId, event });
    } else {
      this.store.navigation.goToNote({ noteId: itemId, event });
    }

    this.close();
  };

  handleSearch = () => {
    const queryString = this.value.trim().replace(/\s+/g, " ");

    if (!queryString) {
      setTimeout(this.close, 0);
      return;
    }

    const activeSearchEngineParams = urlParamsModule.search.parse({
      searchQueryStr: this.store.navigation.activeSearchQuery,
    });

    setTimeout(() => {
      this.store.navigation.goToSearch({
        searchParams: { ...activeSearchEngineParams, queryString },
        retriggerSearch: true,
      });
    }, 10);

    setTimeout(this.close, 0);
  };

  handleAsk = async () => {
    const queryString = this.value.trim().replace(/\s+/g, " ");

    if (!queryString) {
      setTimeout(this.close, 0);
      return;
    }

    if (!this.store.pageStore) {
      setTimeout(this.close, 0);
      return;
    }

    // Get the placeholder session ID
    const sessionId = await this.store.pageStore.memItPage.getPlaceholderSessionId();
    this.store.pageStore.memItPage.setSessionId(sessionId);

    // Create a user message event in the session
    await this.store.sessions.createUserMessageEvent({
      sessionId,
      content: queryString,
    });

    // Navigate to the session in the side panel
    this.store.sidePanel.goToMemIt(sessionId, { openChatPanel: true });

    setTimeout(this.close, 0);
  };
}
